type CommitWithId = {id: string; checked: boolean};

/**
 * Build the URL to navigate to based on the current commit selection.
 */
export function buildApplyUrl(
  {issueLink, queryParams, selectionMode, lastReviewCommitSha, commits, mergeBase}:
  {issueLink: string; queryParams: string; selectionMode: string; lastReviewCommitSha: string | null; commits: CommitWithId[]; mergeBase?: string},
): string {
  if (selectionMode === 'all') {
    return `${issueLink}/files${queryParams}`;
  }
  if (selectionMode === 'sinceReview' && lastReviewCommitSha) {
    return `${issueLink}/files/${lastReviewCommitSha}..${commits.at(-1)!.id}${queryParams}`;
  }
  // Custom selection - find the contiguous range of checked commits
  const firstIdx = commits.findIndex((c) => c.checked);
  const lastIdx = commits.findLastIndex((c) => c.checked);
  if (firstIdx === -1) {
    // Nothing selected, show all
    return `${issueLink}/files${queryParams}`;
  }
  if (firstIdx === 0 && lastIdx === commits.length - 1) {
    // All selected
    return `${issueLink}/files${queryParams}`;
  }
  if (firstIdx === lastIdx) {
    // Single commit
    return `${issueLink}/commits/${commits[firstIdx].id}${queryParams}`;
  }
  // Range starting at first commit: use mergeBase as the range start
  if (firstIdx === 0) {
    if (mergeBase) return `${issueLink}/files/${mergeBase}..${commits[lastIdx].id}${queryParams}`;
    return `${issueLink}/files${queryParams}`;
  }
  // Range with explicit start..end (start is the commit BEFORE the first selected)
  const start = commits[firstIdx - 1].id;
  const end = commits[lastIdx].id;
  return `${issueLink}/files/${start}..${end}${queryParams}`;
}

/**
 * Toggle a commit checkbox while enforcing a contiguous selection range.
 * Returns the new selection mode ('custom' or 'all').
 */
export function toggleCommitSelection(commits: {checked: boolean}[], idx: number): string {
  let selectionMode = 'custom';
  const commit = commits[idx];

  if (commit.checked) {
    const firstIdx = commits.findIndex((c) => c.checked);
    if (idx === firstIdx) {
      // Unchecking the first commit in the range: just remove it
      commit.checked = false;
    } else {
      // Unchecking any other commit: remove it and everything after it
      // to maintain contiguous range from the start
      for (let i = idx; i < commits.length; i++) {
        commits[i].checked = false;
      }
    }
    // If unchecking leaves nothing, go back to 'all'
    if (!commits.some((c) => c.checked)) {
      selectionMode = 'all';
    }
    return selectionMode;
  }

  // Checking: extend the range to include this commit
  const firstIdx = commits.findIndex((c) => c.checked);
  if (firstIdx === -1) {
    // No commits checked yet, just check this one
    commit.checked = true;
    return selectionMode;
  }
  const lastIdx = commits.findLastIndex((c) => c.checked);
  // Fill the contiguous range from min to max
  const rangeStart = Math.min(firstIdx, idx);
  const rangeEnd = Math.max(lastIdx, idx);
  for (let i = 0; i < commits.length; i++) {
    commits[i].checked = i >= rangeStart && i <= rangeEnd;
  }
  return selectionMode;
}
